import { useState } from "react";
import { Check, Copy, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import type { ChangelogEntryRecord } from "./ChangelogEntry";

// Sprint 9.6 — SharePopover : copy lien public + copy Markdown formaté.
// Le lien pointe sur /changelog?week=YYYY-Www pour scroll/anchor (à wirer
// si nécessaire). Le Markdown est le snippet à coller dans Discord / mail.

function entryToMarkdown(e: ChangelogEntryRecord): string {
  const dateLabel = new Date(`${e.released_at}T12:00:00`).toLocaleDateString(
    "fr-FR",
    { day: "2-digit", month: "short", year: "numeric" },
  );
  const typeLabel =
    e.type === "feature"
      ? "Nouveauté"
      : e.type === "fix"
        ? "Correctif"
        : e.type === "breaking"
          ? "Breaking"
          : "Amélioration";
  return `**[${typeLabel}]** ${e.title} _(${dateLabel})_\n${e.description}`;
}

export function ChangelogShare({
  entries,
  weekIso,
}: {
  entries: ChangelogEntryRecord[];
  weekIso?: string;
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedMd, setCopiedMd] = useState(false);

  const copyLink = async () => {
    const url = `${window.location.origin}/#/changelog${weekIso ? `?week=${weekIso}` : ""}`;
    await navigator.clipboard.writeText(url);
    setCopiedLink(true);
    toast.success("Lien copié");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const copyMarkdown = async () => {
    const md = entries.map(entryToMarkdown).join("\n\n");
    await navigator.clipboard.writeText(md);
    setCopiedMd(true);
    toast.success("Markdown copié");
    setTimeout(() => setCopiedMd(false), 2000);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5 h-8"
        >
          <Share2 className="size-3.5" />
          Partager
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        <button
          type="button"
          onClick={copyLink}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-foreground hover:bg-fabrik-green-soft transition-colors"
        >
          {copiedLink ? (
            <Check className="size-3.5 text-fabrik-green" />
          ) : (
            <Link2 className="size-3.5" />
          )}
          Copier le lien
        </button>
        <button
          type="button"
          onClick={copyMarkdown}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-foreground hover:bg-fabrik-green-soft transition-colors"
        >
          {copiedMd ? (
            <Check className="size-3.5 text-fabrik-green" />
          ) : (
            <Copy className="size-3.5" />
          )}
          Copier Markdown
        </button>
      </PopoverContent>
    </Popover>
  );
}
